/**
 * TrackingService.js - Service de tracking des analyses
 * Envoie automatiquement les données au backend
 */

import AnalyticsService from './AnalyticsService';

class TrackingService {
    /**
     * Tracker une analyse effectuée par l'utilisateur
     */
    static async trackAnalysis(analysisData) {
        // Enrichir avec les informations de plateforme
        const trackingPayload = {
            sessionId: analysisData.sessionId || this.generateSessionId(),
            analysisType: analysisData.analysisType,
            medications: analysisData.medications || [],
            results: {
                analysisPerformed: true,
                riskLevel: analysisData.riskLevel || 'UNKNOWN',
                findingsCount: analysisData.findingsCount || 0,
                specificFindings: analysisData.findings || []
            },
            platform: this.getPlatformInfo(),
            patientContext: analysisData.patientContext || {},
            performance: {
                queryDuration: analysisData.queryDuration || 0,
                responseTime: analysisData.responseTime || 0,
                cacheHit: analysisData.cacheHit || false
            },
            userId: this.getUserIdentifier()
        };

        try {
            const result = await AnalyticsService.trackAnalysis(trackingPayload);

            if (result && result.success !== false) {
                const analysisId = result.data?.analysisId || result.data?._id || result.data?.id ||
                    result.analysisId || result._id || result.id;
                if (analysisId) {
                    sessionStorage.setItem('lastAnalysisId', analysisId);
                }
                await this.syncOfflineAnalyses();
                return analysisId || null;
            } else {
                console.error('Le serveur a refusé le suivi de cette analyse.', result.error || result.message || result);
                this.storeOfflineAnalysis(trackingPayload);
                return null;
            }
        } catch (error) {
            console.error('Impossible d’envoyer le suivi de cette analyse.', error);
            this.storeOfflineAnalysis(trackingPayload);
            return null;
        }
    }

    /**
     * Tracker la sauvegarde d'une analyse
     */
    static async trackSave(analysisId, saveData) {
        if (!analysisId) return null;

        try {
            return await AnalyticsService.saveAnalysis(analysisId, {
                saveName: saveData.name || 'Sauvegarde',
                saveNotes: saveData.notes || '',
                saveLocation: saveData.location || '',
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            return null;
        }
    }

    /**
     * Tracker la consultation d'une analyse
     */
    static async trackConsultation(analysisId, timeSpentSeconds = 0) {
        if (!analysisId) return null;

        try {
            return await AnalyticsService.markAsConsidered(analysisId, {
                timeSpentSeconds: Math.round(timeSpentSeconds),
                consultedAt: new Date().toISOString(),
                userAction: 'VIEW_DETAILS'
            });
        } catch (error) {
            return null;
        }
    }

    /**
     * Tracker le temps passé sur une analyse
     */
    static async trackTimeSpent(analysisId, startTime) {
        if (!analysisId || !startTime) return;

        const timeSpent = (Date.now() - startTime) / 1000;
        if (timeSpent < 1) return;

        return await this.trackConsultation(analysisId, timeSpent);
    }

    /**
     * Récupérer un identifiant utilisateur lisible (email > displayName > UID > anonymous)
     */
    static getUserIdentifier() {
        try {
            const lastUser = localStorage.getItem('lastLoggedUser');
            if (lastUser) {
                const userData = JSON.parse(lastUser);
                if (userData.email) return userData.email;
                if (userData.displayName) return userData.displayName;
                if (userData.name) return userData.name;
                if (userData.uid) return userData.uid;
            }
        } catch (e) {}
        return localStorage.getItem('userId')
            || localStorage.getItem('authUserId')
            || 'anonymous';
    }

    /**
     * Générer un ID de session unique
     */
    static generateSessionId() {
        let sessionId = sessionStorage.getItem('analyticsSessionId');
        if (!sessionId) {
            sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('analyticsSessionId', sessionId);
        }
        return sessionId;
    }

    /**
     * Récupérer les informations de la plateforme
     */
    static getPlatformInfo() {
        const userAgent = navigator.userAgent;
        const isMobile = /mobile|android|iphone|ipad|ipod/i.test(userAgent);
        const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent);

        let deviceType = 'DESKTOP';
        if (isMobile) deviceType = 'MOBILE';
        if (isTablet) deviceType = 'TABLET';

        return {
            deviceType: deviceType,
            osType: this.getOS(),
            osVersion: this.getOSVersion(),
            browserName: this.getBrowser(),
            browserVersion: this.getBrowserVersion(),
            appVersion: process.env.REACT_APP_VERSION || '1.0.0',
            location: {
                country: this.getCountry(),
                region: sessionStorage.getItem('userRegion') || '',
                city: sessionStorage.getItem('userCity') || '',
                department: sessionStorage.getItem('userDepartment') || '',
                hospital: sessionStorage.getItem('hospitalName') || ''
            }
        };
    }

    static getOS() {
        const ua = navigator.userAgent;
        if (ua.includes('Windows')) return 'Windows';
        if (ua.includes('Mac')) return 'macOS';
        if (ua.includes('Linux')) return 'Linux';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
        return 'Unknown';
    }

    static getOSVersion() {
        const ua = navigator.userAgent;
        const match = ua.match(/(Windows NT |Mac OS X |Android |iOS )([\d._]+)/);
        return match ? match[2] : '';
    }

    static getBrowser() {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
        if (ua.includes('Edg')) return 'Edge';
        if (ua.includes('Opera')) return 'Opera';
        return 'Other';
    }

    static getBrowserVersion() {
        const ua = navigator.userAgent;
        const browser = this.getBrowser();
        let regex;
        switch(browser) {
            case 'Chrome': regex = /Chrome\/([\d.]+)/; break;
            case 'Firefox': regex = /Firefox\/([\d.]+)/; break;
            case 'Safari': regex = /Version\/([\d.]+)/; break;
            case 'Edge': regex = /Edg\/([\d.]+)/; break;
            default: return '';
        }
        const match = ua.match(regex);
        return match ? match[1] : '';
    }

    static getCountry() {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return timeZone.split('/')[0];
        } catch {
            return '';
        }
    }

    /**
     * Stocker les analyses en mode offline
     */
    static storeOfflineAnalysis(analysisData) {
        try {
            const offlineAnalyses = JSON.parse(localStorage.getItem('offlineAnalyses') || '[]');
            offlineAnalyses.push({
                ...analysisData,
                timestamp: new Date().toISOString()
            });
            // Limiter à 100 analyses offline max
            if (offlineAnalyses.length > 100) offlineAnalyses.shift();
            localStorage.setItem('offlineAnalyses', JSON.stringify(offlineAnalyses));
        } catch (error) {}
    }

    /**
     * Synchroniser les analyses offline
     */
    static async syncOfflineAnalyses() {
        let offlineAnalyses;
        try {
            offlineAnalyses = JSON.parse(localStorage.getItem('offlineAnalyses') || '[]');
        } catch (error) {
            console.error('Impossible de lire les analyses en attente de synchronisation.', error);
            return;
        }
        if (offlineAnalyses.length === 0) return;

        const pendingAnalyses = [];
        for (const analysis of offlineAnalyses) {
            try {
                const result = await AnalyticsService.trackAnalysis(analysis);
                if (result && result.success !== false) {
                    continue;
                }
                console.error('La synchronisation du suivi a été refusée par le serveur.', result && (result.error || result.message || result));
                pendingAnalyses.push(analysis);
            } catch (error) {
                console.error('Impossible de synchroniser un suivi hors ligne.', error);
                pendingAnalyses.push(analysis);
            }
        }

        try {
            if (pendingAnalyses.length === 0) {
                localStorage.removeItem('offlineAnalyses');
            } else {
                localStorage.setItem('offlineAnalyses', JSON.stringify(pendingAnalyses));
            }
        } catch (error) {
            console.error('Impossible de mettre à jour les analyses en attente de synchronisation.', error);
        }
    }

    /**
     * Vérifier si le tracking est actif
     */
    static async checkTrackingStatus() {
        try {
            const today = new Date().toISOString().split('T')[0];
            const stats = await AnalyticsService.getGlobalStatistics(today, today);
            return !!(stats?.data?.totalStats?.[0]?.totalAnalyses > 0);
        } catch (error) {
            return false;
        }
    }
}

export default TrackingService;